import { useEffect } from 'react';
import { useDevice } from './store';
import { useToasts } from './lib/toast';
import { RoleSelect } from './roles/RoleSelect';
import type { DeviceRole } from '@taj/types';

// Liens directs par rôle : http://…/#backoffice , #caisse , #borne , #salle …
const HASH_ROLES: Record<string, DeviceRole> = {
  backoffice: 'BACKOFFICE', admin: 'BACKOFFICE', manager: 'BACKOFFICE',
  caisse: 'CASHIER', cashier: 'CASHIER',
  borne: 'KIOSK', kiosk: 'KIOSK',
  salle: 'DISPLAY', display: 'DISPLAY', ecran: 'DISPLAY',
  comptoir: 'PICKUP', retrait: 'PICKUP', pickup: 'PICKUP',
};
// Liens cuisine par poste : #cuisine-thai , #cuisine-indien , #cuisine-naan
const KDS_STATIONS: Record<string, string> = {
  thai: 'Thaï', indien: 'Indien', naan: 'Naan',
};
// Liens borne par imprimante : #borne-1 , #borne-2 , #borne-3 … (le ticket sort sur l'imprimante « Borne N »)
import { Kiosk } from './roles/kiosk/Kiosk';
import { Cashier } from './roles/cashier/Cashier';
import { Kds } from './roles/kds/Kds';
import { Display } from './roles/display/Display';
import { Comptoir } from './roles/pickup/Comptoir';
import { BackOffice } from './roles/backoffice/BackOffice';

function Toasts() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <div className="tw">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.type}`}>
          {t.type === 'warn' ? '⚠️ ' : '✅ '}
          {t.msg}
        </div>
      ))}
    </div>
  );
}

export function App() {
  const role = useDevice((s) => s.role);
  const setRole = useDevice((s) => s.setRole);
  const resetRole = useDevice((s) => s.resetRole);

  // Lien direct : applique le rôle indiqué dans l'URL (#backoffice, #caisse…)
  useEffect(() => {
    const apply = () => {
      const key = location.hash.replace(/[#/]/g, '').toLowerCase();
      if (!key) return; // pas de hash → on garde le rôle en cours
      // Cuisine par poste : #cuisine-thai , #kds-indien …
      const kds = key.match(/^(?:kds|cuisine)-(.+)$/);
      if (kds && KDS_STATIONS[kds[1]]) {
        setRole('KDS', KDS_STATIONS[kds[1]]);
        return;
      }
      // Cuisine sans poste (#cuisine , #kds) → écran de choix du poste (Thaï / Indien / Naan).
      if (key === 'cuisine' || key === 'kds' || key === 'kitchen') {
        resetRole();
        return;
      }
      // Borne numérotée : #borne-1 , #borne-2 , #borne-3 … → poste imprimante « Borne N »
      const borne = key.match(/^borne-(\d+)$/);
      if (borne) {
        setRole('KIOSK', `Borne ${borne[1]}`);
        return;
      }
      const target = HASH_ROLES[key];
      // Hash reconnu → on applique. Hash inconnu → on montre le menu de choix plutôt que
      // de rester bloqué sur l'écran précédent (source de confusion).
      if (target) setRole(target);
      else resetRole();
    };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, [setRole, resetRole]);

  let screen;
  switch (role) {
    case 'KIOSK':
      screen = <Kiosk />;
      break;
    case 'CASHIER':
      screen = <Cashier />;
      break;
    case 'KDS':
      screen = <Kds />;
      break;
    case 'DISPLAY':
      screen = <Display />;
      break;
    case 'PICKUP':
      screen = <Comptoir />;
      break;
    case 'BACKOFFICE':
      screen = <BackOffice />;
      break;
    default:
      screen = <RoleSelect />;
  }

  return (
    <>
      {screen}
      <Toasts />
    </>
  );
}
